import React, { useCallback, useState } from 'react';
import RingingBellIcon from 'ui/Icons/ringing-bell.component.svg';
import classnames from 'classnames';
import { theme } from '../../../../../tailwind.config';
import { HidingTextTooltip } from 'ui/Tooltip/HidingTextTooltip';
import { HidingTooltip } from 'ui/Tooltip';
import { SubscriptionWindow } from './SubscriptionWindow';
import { IDirectoryEntry } from 'services/CrmApi/types/DirectoryEntry';
import { useDirectoryEntrySubscribeMutation, useDirectoryEntryUnsubscribeMutation } from 'services/CrmApi/mutations/useDirectoryEntrySubscriptions';
import { IUser } from 'services/BackendApi';
import { useQueryClient } from '@tanstack/react-query';
import { useNotifications } from 'hooks/useNotifications';

export interface ISubscriptionBellButtonProps {
  directoryEntry: IDirectoryEntry;
  isSubscribed: boolean;
  disabled?: boolean;
  currentUser: IUser;
}

export const SubscriptionBellButton: React.FC<ISubscriptionBellButtonProps> = ({ directoryEntry, currentUser, isSubscribed, disabled = false }) => {
  const subscribers = directoryEntry.subscribers ?? [];

  const { showSuccessNotification, showErrorNotification } = useNotifications();

  const [isSubscriptionWindowOpen, setIsSubscriptionWindowOpen] = useState(false);
  const bellColor = disabled
    ? theme.colors['gray-60']
    : isSubscribed
    ? theme.colors['green-hard']
    : theme.colors['brown-prime'];
  const buttonClassname = classnames(
    'subscription-bell-button flex items-center justify-center min-h-[33px] rounded active:border-2 active:border-solid active:border-teal-80 gap-[5px]',
    {
      'bg-teal-20': isSubscribed && !disabled,
      'bg-gray-10': !isSubscribed || disabled,
      'cursor-pointer active:px-[10px] active:py-[5px]': !disabled,
      'px-[12px] py-[7px]': !isSubscriptionWindowOpen,
      'border-2 border-solid border-teal-80 px-[10px] py-[5px]': isSubscriptionWindowOpen,
    }
  );

  const directoryEntrySubscribeMutation = useDirectoryEntrySubscribeMutation();
  const directoryEntryUnsubscribeMutation = useDirectoryEntryUnsubscribeMutation();
  const queryClient = useQueryClient();
  const isSubscribing = directoryEntrySubscribeMutation.isPending || directoryEntryUnsubscribeMutation.isPending;
  const isLoadingDirectoryEntry = queryClient.isFetching({ queryKey: ['crm-directory-entry', directoryEntry.id] }) > 0;

  const handleSubscribe = useCallback(async () => {
    try {
      await directoryEntrySubscribeMutation.mutateAsync({ directoryId: directoryEntry.id, beUserId: currentUser.uuid });
      showSuccessNotification('Successfully subscribed to notifications.');
      queryClient.invalidateQueries({ queryKey: ['crm-directory-entry', directoryEntry.id] });
    } catch (error) {
      console.error(error);
      showErrorNotification('There was an error subscribing to notifications.');
    }
  }, [directoryEntrySubscribeMutation, currentUser.uuid, directoryEntry.id, queryClient]);

  const handleUnsubscribe = useCallback(async () => {
    try {
      await directoryEntryUnsubscribeMutation.mutateAsync({ directoryId: directoryEntry.id, beUserId: currentUser.uuid });
      showSuccessNotification('Successfully unsubscribed from notifications.');
      queryClient.invalidateQueries({ queryKey: ['crm-directory-entry', directoryEntry.id] });
    } catch (error) {
      console.error(error);
      showErrorNotification('There was an error unsubscribing from notifications.');
    }
  }, [directoryEntryUnsubscribeMutation, currentUser.uuid, directoryEntry.id, queryClient]);

  const handleSubscribeOrUnsubscribe = useCallback(async () => {
    if (disabled) {
      return;
    }
    if (isSubscribed) {
      await handleUnsubscribe();
    } else {
      await handleSubscribe();
    }
  }, [isSubscribed, handleSubscribe, handleUnsubscribe, disabled]);

  const renderTooltipContent = useCallback(() => {
    return (
      <SubscriptionWindow
        subscribedUsers={subscribers}
        onButtonClick={handleSubscribeOrUnsubscribe}
        isLoading={isSubscribing || isLoadingDirectoryEntry}
        isSubscribed={isSubscribed}
      />
    );
  }, [subscribers, handleSubscribeOrUnsubscribe, isSubscribing, isLoadingDirectoryEntry]);

  const notifyOpenClosedState = useCallback((isOpen: boolean) => {
    setIsSubscriptionWindowOpen(isOpen);
  }, []);

  return (
    <HidingTooltip
      renderTooltipContent={renderTooltipContent}
      showOnClick
      hideOnClick
      position="bottom-left"
      elementClassname="justify-center"
      tooltipClassname="o:absolute"
      usePortal
      notifyOpenClosedState={notifyOpenClosedState}
    >
      <div className="subscription-bell-button-container">
        <HidingTextTooltip
          tooltipContent="Subscribe to receive notifications."
          position="bottom-left"
          elementClassname="justify-center"
          tooltipContentClassname="py-[5px] px-[8px]"
          enabled={!isSubscriptionWindowOpen}
        >
          <div className={buttonClassname}>
            <RingingBellIcon className="w-[18px] h-[18px]" fill={bellColor} />
            {subscribers.length > 1 && <span className="font-pt-sans text-[15px] text-black">{subscribers.length}</span>}
          </div>
        </HidingTextTooltip>
      </div>
    </HidingTooltip>
  );
};
